<script setup lang="ts">
import {
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  LoaderCircle,
  Search,
  ShieldCheck,
  UserRoundCog,
  X,
} from "lucide-vue-next"
import { onMounted, ref } from "vue"

import AppActionDialog from "../../components/AppActionDialog.vue"
import AppSelect from "../../components/AppSelect.vue"
import { ApiError, apiRequest } from "../../lib/api"
import { formatShanghaiTime } from "../../lib/datetime"
import type { AdminUserItem, AdminUserList, AdminWorkspaceItem } from "../../lib/types"
import { useSessionStore } from "../../stores/session"

const session = useSessionStore()
const users = ref<AdminUserItem[]>([])
const total = ref(0)
const loading = ref(true)
const search = ref("")
const statusFilter = ref("")
const expanded = ref(new Set<string>())
const error = ref("")
const creditTarget = ref<{ user: AdminUserItem; workspace: AdminWorkspaceItem } | null>(null)
const creditAmount = ref(1000)
const creditReason = ref("运营手动调整")
const submitting = ref(false)
const updateTarget = ref<{
  user: AdminUserItem
  patch: Record<string, string>
  label: string
} | null>(null)
const userStatusOptions = [
  { value: "", label: "全部状态" },
  { value: "active", label: "活跃" },
  { value: "disabled", label: "已禁用" },
  { value: "deleted", label: "已删除" },
]
onMounted(loadUsers)

async function loadUsers(): Promise<void> {
  loading.value = true
  error.value = ""
  const params = new URLSearchParams()
  if (search.value.trim()) params.set("search", search.value.trim())
  if (statusFilter.value) params.set("status", statusFilter.value)
  try {
    const result = await apiRequest<AdminUserList>(`/v1/admin/users?${params}`)
    users.value = result.items
    total.value = result.total
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "加载用户失败"
  } finally {
    loading.value = false
  }
}

function toggleExpanded(id: string): void {
  const next = new Set(expanded.value)
  next.has(id) ? next.delete(id) : next.add(id)
  expanded.value = next
}

async function updateUser(): Promise<void> {
  if (!updateTarget.value) return
  const { user, patch } = updateTarget.value
  submitting.value = true
  try {
    const updated = await apiRequest<AdminUserItem>(`/v1/admin/users/${user.id}`, {
      method: "PATCH",
      csrfToken: session.csrfToken,
      body: patch,
    })
    users.value = users.value.map((item) => (item.id === updated.id ? updated : item))
    updateTarget.value = null
  } catch (cause) {
    error.value = cause instanceof ApiError ? cause.message : "更新失败"
  } finally {
    submitting.value = false
  }
}

async function adjustCredits(): Promise<void> {
  if (!creditTarget.value) return
  submitting.value = true
  error.value = ""
  try {
    await apiRequest(`/v1/admin/workspaces/${creditTarget.value.workspace.id}/credits`, {
      method: "POST",
      csrfToken: session.csrfToken,
      body: {
        amount: creditAmount.value,
        reason: creditReason.value,
      },
    })
    creditTarget.value = null
    await loadUsers()
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "额度调整失败"
  } finally {
    submitting.value = false
  }
}

function formatDate(value: string | null): string {
  if (!value) return "从未"
  return formatShanghaiTime(value, { dateStyle: "medium", timeStyle: "short" })
}
</script>

<template>
  <section class="admin-page">
    <div class="admin-page-heading">
      <div><span class="eyebrow">Identity & credits</span><h1>用户与额度</h1><p>管理系统访问状态、管理员权限与 Workspace 积分余额。</p></div>
      <div class="admin-count"><strong>{{ total }}</strong><span>用户</span></div>
    </div>

    <div class="admin-toolbar">
      <label class="admin-search"><Search :size="15" /><input v-model="search" placeholder="搜索邮箱或名称" @keyup.enter="loadUsers" /></label>
      <AppSelect v-model="statusFilter" class="admin-select" compact aria-label="用户状态筛选" :options="userStatusOptions" @change="loadUsers" />
      <button class="secondary-button" @click="loadUsers">查询</button>
    </div>

    <p v-if="error" class="form-error">{{ error }}</p>
    <div class="admin-table-shell">
      <div v-if="loading" class="admin-loading"><LoaderCircle class="spin" :size="20" />正在加载用户</div>
      <div v-else-if="users.length === 0" class="admin-empty">没有符合条件的用户。</div>
      <div v-else class="responsive-table">
        <table class="admin-table">
          <thead><tr><th>用户</th><th>角色</th><th>状态</th><th>Workspace</th><th>最近登录</th><th>操作</th></tr></thead>
          <tbody>
            <template v-for="user in users" :key="user.id">
              <tr>
                <td><button class="row-expander" @click="toggleExpanded(user.id)"><ChevronDown v-if="expanded.has(user.id)" :size="15" /><ChevronRight v-else :size="15" /><span><strong>{{ user.display_name || user.email.split("@")[0] }}</strong><small>{{ user.email }}</small></span></button></td>
                <td><span class="role-badge" :class="{ 'role-badge--admin': user.system_role === 'admin' }"><ShieldCheck v-if="user.system_role === 'admin'" :size="12" />{{ user.system_role }}</span></td>
                <td><span class="status-pill" :class="{ 'status-pill--active': user.status === 'active' }"><i></i>{{ user.status }}</span></td>
                <td>{{ user.workspaces.length }}</td>
                <td>{{ formatDate(user.last_login_at) }}</td>
                <td><div class="table-actions"><button class="text-button" @click="updateTarget = { user, patch: { system_role: user.system_role === 'admin' ? 'user' : 'admin' }, label: user.system_role === 'admin' ? '降为普通用户' : '设为系统管理员' }">{{ user.system_role === "admin" ? "降为用户" : "设为管理员" }}</button><button class="text-button" @click="updateTarget = { user, patch: { status: user.status === 'active' ? 'disabled' : 'active' }, label: user.status === 'active' ? '禁用用户' : '启用用户' }">{{ user.status === "active" ? "禁用" : "启用" }}</button></div></td>
              </tr>
              <tr v-if="expanded.has(user.id)" class="workspace-detail-row">
                <td colspan="6">
                  <div class="workspace-admin-list">
                    <article v-for="workspace in user.workspaces" :key="workspace.id">
                      <div><UserRoundCog :size="17" /><span><strong>{{ workspace.name }}</strong><code>{{ workspace.id }}</code></span></div>
                      <dl><div><dt>计划</dt><dd>{{ workspace.plan_code }}</dd></div><div><dt>可用额度</dt><dd>{{ workspace.credits.toLocaleString() }}</dd></div></dl>
                      <button class="secondary-button" @click="creditTarget = { user, workspace }"><CircleDollarSign :size="15" />调整额度</button>
                    </article>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="creditTarget" class="modal-backdrop" @click.self="creditTarget = null">
      <form class="modal" @submit.prevent="adjustCredits">
        <div class="modal__heading"><div><span class="eyebrow">Credit adjustment</span><h2>调整用户额度</h2><p>{{ creditTarget.user.email }} · {{ creditTarget.workspace.name }}</p></div><button type="button" class="icon-button" @click="creditTarget = null"><X :size="18" /></button></div>
        <div class="admin-form-grid">
          <label class="field"><span>调整数量</span><input v-model.number="creditAmount" type="number" min="-10000000" max="10000000" required /><small>正数增加，负数扣减。</small></label>
        </div>
        <label class="field"><span>操作原因</span><input v-model="creditReason" minlength="3" maxlength="255" required /></label>
        <div class="modal__actions"><button type="button" class="secondary-button" @click="creditTarget = null">取消</button><button class="primary-button" :disabled="submitting"><LoaderCircle v-if="submitting" class="spin" :size="16" />确认调整</button></div>
      </form>
    </div>

    <AppActionDialog
      :open="Boolean(updateTarget)"
      eyebrow="User permission"
      title="确认用户权限变更"
      description="该操作会立即生效，用户当前会话可能会被撤销。"
      :detail="updateTarget ? `${updateTarget.label} · ${updateTarget.user.email}` : ''"
      confirm-label="确认变更"
      :danger="updateTarget?.patch.status === 'disabled' || updateTarget?.patch.system_role === 'user'"
      :busy="submitting"
      @close="updateTarget = null"
      @confirm="updateUser"
    />
  </section>
</template>
